import { AIAgentType } from '../../../models/AIAgent'
import {
  type AccountOpeningCollection,
  type AccountOpeningConfirmScope,
  type AccountOpeningDescriptor,
  type AccountOpeningFlowMode,
  type AccountOpeningItemLayout,
  type AccountOpeningPanelKind,
  type AccountOpeningStep,
  type AccountOpeningStepKind,
} from '../../../models/AccountOpeningDescriptor'
import { schemaValueSource, SCHEMA_KEYS } from '../domain/collections'

const AGENT_TYPES = new Set<string>(Object.values(AIAgentType))

const STEP_KINDS: AccountOpeningStepKind[] = ['collection', 'operation', 'handoff']
const PANEL_KINDS: AccountOpeningPanelKind[] = ['typeSummary', 'schemaForm', 'holders']

const COLLECTIONS: AccountOpeningCollection[] = ['accounts', 'parties']
const ITEM_LAYOUTS: AccountOpeningItemLayout[] = ['card', 'row']
const CONFIRM_SCOPES: AccountOpeningConfirmScope[] = ['block', 'item']

const FLOW_MODES: AccountOpeningFlowMode[] = ['edit', 'readOnly']

const ACCOUNTS_ONLY_PANELS: AccountOpeningPanelKind[] = ['typeSummary', 'holders']

export const descriptorIssues = (descriptor: AccountOpeningDescriptor): string[] => {
  const issues: string[] = []

  const seenIds = new Set<string>()
  descriptor.steps.forEach((step) => {
    if (seenIds.has(step.id)) {
      issues.push(`step id "${step.id}" is used twice, so the two steps would share their status and confirmations`)
    }
    seenIds.add(step.id)
  })

  const declaredGates = new Set(descriptor.steps.flatMap((step) => (step.gate ? [step.gate] : [])))

  descriptor.steps.forEach((step) => {
    if (!STEP_KINDS.includes(step.kind)) {
      issues.push(`step "${step.id}" is of kind "${step.kind}", which this build does not render`)
      return
    }

    ;(step.requires ?? []).forEach((gate) => {
      if (!declaredGates.has(gate)) {
        issues.push(`step "${step.id}" requires gate "${gate}", which no step in this descriptor declares, so it would never open`)
      }
    })

    const modes = step.modes ?? []
    if (modes.length === 0) {
      issues.push(`step "${step.id}" is in no flow, so it never renders`)
    }
    modes.forEach((mode) => {
      if (!FLOW_MODES.includes(mode)) {
        issues.push(`step "${step.id}" is in flow "${mode}", which this build does not render`)
      }
    })

    if (step.kind === 'collection') {
      issues.push(...collectionStepIssues(step))
      return
    }

    if (step.kind === 'operation') {
      if (!step.operation) {
        issues.push(`step "${step.id}" has a blank operation, so it has nowhere to post`)
      }
      if (!step.gate) {
        issues.push(`step "${step.id}" posts to the connector but declares no gate, so its verdict would gate nothing`)
      }
    }
  })

  const irreversible = descriptor.steps.filter((step) => step.kind === 'operation' && step.irreversible)
  if (irreversible.length > 1) {
    issues.push(`steps ${irreversible.map((step) => `"${step.id}"`).join(', ')} are all irreversible, but only one step's result can be the submission`)
  }

  COLLECTIONS.forEach((collection) => {
    const sources = descriptor.steps.flatMap((step) =>
      step.kind === 'collection' && step.collection === collection
        ? (step.panels ?? []).flatMap((panel) => (panel.kind === 'schemaForm' ? [`${panel.entity}/${panel.keyedBy}/${schemaValueSource(panel)}`] : []))
        : [],
    )
    const distinct = [...new Set(sources)]
    if (distinct.length > 1) {
      issues.push(`the "${collection}" collection is read from more than one schema source (${distinct.join(', ')}), but an item holds only one schema`)
    }
  })

  descriptor.steps.forEach((step) => {
    const prefill = step.kind === 'collection' ? step.prefill : undefined
    if (prefill && !AGENT_TYPES.has(prefill.agent)) {
      issues.push(`step "${step.id}" prefills with agent "${prefill.agent}", which this build does not implement`)
    }
  })

  FLOW_MODES.forEach((mode) => {
    if (!descriptor.steps.some((step) => (step.modes ?? []).includes(mode))) {
      issues.push(`no step is in the "${mode}" flow, so it would render nothing`)
    }
  })

  if (!descriptor.entities.accountType) {
    issues.push('entities.accountType is empty, so the account-type catalog cannot be read')
  }
  if (!descriptor.entities.accountTypeSelector) {
    issues.push('entities.accountTypeSelector is empty, so the catalog axes cannot be read')
  }

  return issues
}

const collectionStepIssues = (step: AccountOpeningStep & { kind: 'collection' }): string[] => {
  const issues: string[] = []

  if (!COLLECTIONS.includes(step.collection)) {
    issues.push(`step "${step.id}" repeats over collection "${step.collection}", which this build does not hold`)
  }
  if (!ITEM_LAYOUTS.includes(step.itemLayout)) {
    issues.push(`step "${step.id}" uses item layout "${step.itemLayout}", which this build does not render`)
  }

  const panels = step.panels ?? []
  if (panels.length === 0) {
    issues.push(`step "${step.id}" declares no panels, so its items would render empty`)
  }

  panels.forEach((panel, index) => {
    if (!PANEL_KINDS.includes(panel.kind)) {
      issues.push(`step "${step.id}" panel ${index} is of kind "${panel.kind}", which this build does not render`)
      return
    }
    if (ACCOUNTS_ONLY_PANELS.includes(panel.kind) && step.collection !== 'accounts') {
      issues.push(`step "${step.id}" panel ${index} is "${panel.kind}", which only applies to the accounts collection`)
    }
    if (panel.kind === 'schemaForm') {
      if (!panel.entity) {
        issues.push(`step "${step.id}" panel ${index} has a blank entity, so its schema cannot be read`)
      }
      if (!panel.keyedBy) {
        issues.push(`step "${step.id}" panel ${index} has a blank keyedBy, so its schema request has no param name`)
      }
      if (!schemaValueSource(panel)) {
        const asked = panel.valueFrom ?? panel.keyedBy
        issues.push(`step "${step.id}" panel ${index} takes its schema's value from "${asked}", which is none of ${SCHEMA_KEYS.join(', ')}`)
      }
      if (panel.include && panel.exclude) {
        issues.push(`step "${step.id}" panel ${index} sets both include and exclude, so one of them would be ignored`)
      }
    }
  })

  if (step.confirm) {
    if (!CONFIRM_SCOPES.includes(step.confirm.scope)) {
      issues.push(`step "${step.id}" confirms at scope "${step.confirm.scope}", which this build does not implement`)
    }
    if (!step.gate) {
      issues.push(`step "${step.id}" asks for a confirmation but declares no gate, so signing it off would unlock nothing`)
    }
  }

  return issues
}
